import { IconChevronLeft, IconChevronRight } from "@tabler/icons-react";
import { useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "./Button";

type Overflow = "none" | "end" | "start";

/**
 * Which way "further" scrolls: in a right-to-left page (Arabic) the hidden
 * chips sit on the left, and `scrollLeft` runs from 0 down to minus the
 * overflow.
 */
function inlineSign(el: HTMLElement): 1 | -1 {
  return getComputedStyle(el).direction === "rtl" ? -1 : 1;
}

/**
 * A desktop row of chips on one line (Figma "chips" + "IconButton · next"):
 * the chips scroll sideways, and an outline Button at the row's end moves
 * them. It reads "next" while chips hide past the end, then "back to the
 * start" once the end is reached, so a mouse can always return. With no
 * overflow, the row is the chips alone.
 */
export function DesktopChipRow({
  children,
}: {
  children: ReactNode;
}): React.JSX.Element {
  const { t } = useTranslation();
  const viewportRef = useRef<HTMLDivElement>(null);
  const [overflow, setOverflow] = useState<Overflow>("none");

  useLayoutEffect(() => {
    const viewport = viewportRef.current;
    if (viewport === null) return;
    const content = viewport.firstElementChild;

    function update(): void {
      if (viewport === null) return;
      const maxScroll = viewport.scrollWidth - viewport.clientWidth;
      const scrolled = viewport.scrollLeft * inlineSign(viewport);
      const next: Overflow =
        maxScroll <= 1 ? "none" : scrolled < maxScroll - 1 ? "end" : "start";
      setOverflow(next);
    }

    update();
    viewport.addEventListener("scroll", update, { passive: true });
    // The chips land after the fonts and the server's order: watch both the
    // viewport and its content.
    const observer = new ResizeObserver(update);
    observer.observe(viewport);
    if (content !== null) observer.observe(content);
    return () => {
      viewport.removeEventListener("scroll", update);
      observer.disconnect();
    };
  }, []);

  function move(): void {
    const viewport = viewportRef.current;
    if (viewport === null) return;
    switch (overflow) {
      case "end":
        viewport.scrollBy({
          left: viewport.clientWidth * 0.8 * inlineSign(viewport),
          behavior: "smooth",
        });
        return;
      case "start":
        viewport.scrollTo({ left: 0, behavior: "smooth" });
        return;
      case "none":
        return;
      default:
        throw new Error(overflow satisfies never);
    }
  }

  return (
    <div className="flex items-center gap-2">
      <div
        ref={viewportRef}
        className="min-w-0 flex-1 overflow-x-auto scrollbar-hidden"
      >
        <div className="flex w-max items-center gap-2 py-1.25">{children}</div>
      </div>
      {overflow === "none" ? null : (
        <Button
          type="button"
          variant="outline"
          size="sm"
          aria-label={overflow === "end" ? t("Next") : t("Previous")}
          onClick={move}
          className="size-8 shrink-0 px-0"
        >
          {/* The chevron points along the reading direction. */}
          {overflow === "end" ? (
            <IconChevronRight className="rtl:rotate-180" stroke={1.5} />
          ) : (
            <IconChevronLeft className="rtl:rotate-180" stroke={1.5} />
          )}
        </Button>
      )}
    </div>
  );
}
